<template>
  <modal
    :show="isVisible"
    :title="t('Selecionar Recebimentos em Dinheiro')"
    :close-label="t('Cancelar')"
    :save-label="convertLabel"
    :save-fn="handleConvert"
    :save-disabled-fn="() => selectedRecebimentos.size === 0 || isConverting"
    size="large"
    elem-id="modal_cash_advances_selection"
    data-testid="payroll-shared-cash-advances-dialog"
    @close="closeDialog"
    @dismiss="closeDialog"
  >
    <div v-if="isVisible" class="cash-advances-selection">
      <!-- Info Message -->
      <inline-message severity="info" class="mb-3">
        <i18n-t keypath="cash_advantages_selection_info">
          <template #br><br></template>
        </i18n-t>
      </inline-message>

      <!-- Filters -->
      <div class="filter-row mb-3">
        <div class="filter-col">
          <input-text
            v-model="searchQuery"
            elem-id="cash-advances-search"
            :label="t('Search')"
            :placeholder="t('Search by employee or client name')"
            data-testid="payroll-shared-cash-advances-search"
          />
        </div>
        <div class="filter-col">
          <select-list
            v-model="sortBy"
            elem-id="cash-advances-sort"
            :label="t('Sort by')"
            :options="sortOptions"
            option-label="label"
            option-value="value"
            data-testid="payroll-shared-cash-advances-sort"
          />
        </div>
      </div>

      <!-- Selection Controls -->
      <div class="selection-controls mb-3">
        <div class="selection-buttons">
          <AppButton
            :label="t('Select All')"
            icon="pi pi-check-square"
            severity="secondary"
            size="small"
            :disabled="filteredRecebimentos.length === 0"
            data-testid="payroll-shared-cash-advances-select-all-btn"
            @click="selectAllVisible"
          />
          <AppButton
            :label="t('Clear Selection')"
            icon="pi pi-times"
            severity="secondary"
            size="small"
            :disabled="selectedRecebimentos.size === 0"
            data-testid="payroll-shared-cash-advances-clear-btn"
            @click="clearSelection"
          />
        </div>
        <span class="selection-count">
          {{ t('Selected') }}: <strong>{{ selectedRecebimentos.size }}</strong> / {{ filteredRecebimentos.length }}
        </span>
      </div>

      <!-- Table -->
      <DataTable
        :value="filteredRecebimentos"
        v-model:selection="selectedRecebimentosArray"
        dataKey="recebimento_id"
        :paginator="true"
        :rows="10"
        :rowsPerPageOptions="[10, 20, 50]"
        :loading="isLoading"
        :emptyMessage="t('No cash recebimentos found')"
        selectionMode="multiple"
        scrollable
        scrollHeight="400px"
        class="p-datatable-sm"
        data-testid="payroll-shared-cash-advances-table"
      >
        <Column selectionMode="multiple" headerStyle="width: 3rem"></Column>
        <Column field="operador_name" :header="t('Employee')" sortable style="min-width: 200px">
          <template #body="{ data }">
            <div class="flex align-items-center gap-2">
              <i class="pi pi-user text-600"></i>
              <span>{{ data.operador_name }}</span>
            </div>
          </template>
        </Column>
        <Column field="recebimento_data" :header="t('Date')" sortable style="min-width: 120px">
          <template #body="{ data }">
            {{ formatDateStr(data.recebimento_data) }}
          </template>
        </Column>
        <Column field="amount_reais" :header="t('Amount')" sortable style="min-width: 120px">
          <template #body="{ data }">
            <span class="font-bold text-green-600">{{ formatCurrency(data.amount_reais) }}</span>
          </template>
        </Column>
        <Column field="cliente_nome" :header="t('Client')" sortable style="min-width: 200px">
          <template #body="{ data }">
            <div v-if="data.cliente_nome" class="flex align-items-center gap-2">
              <i class="pi pi-user-minus text-600"></i>
              <span>{{ data.cliente_nome }}</span>
            </div>
            <span v-else class="text-400 italic">-</span>
          </template>
        </Column>
        <Column field="forma_pagamento_nome" :header="t('Payment Method')" style="min-width: 150px">
          <template #body="{ data }">
            <Tag :value="data.forma_pagamento_nome" severity="success" icon="pi pi-money-bill" />
          </template>
        </Column>
      </DataTable>

      <!-- Summary -->
      <div v-if="selectedRecebimentos.size > 0" class="selection-summary mt-3">
        <span class="text-lg font-semibold">{{ t('Total Selected') }}:</span>
        <span class="text-xl font-bold text-green-600 ml-2">{{ formatCurrency(totalSelectedAmount) }}</span>
        <span class="text-600 ml-2">({{ selectedRecebimentos.size }} {{ t('recebimentos') }})</span>
      </div>
    </div>
  </modal>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../../../eventBus';
import { useEmployeeAdvances } from '../../../../composables/useEmployeeAdvances';
import { useDateFormatter } from '../../../../composables/useDateFormatter';
import { log } from '../../../../utils/DebugLogger';
import { usePayrollAdvances } from '../../../../composables/usePayrollAdvances';

interface Recebimento {
  recebimento_id: number | string;
  operador_name?: string;
  cliente_nome?: string;
  recebimento_data?: string;
  amount_reais: number;
  forma_pagamento_nome?: string;
  [key: string]: unknown;
}

interface ConvertResult {
  created_count: number;
  [key: string]: unknown;
}

interface OpenPayload {
  periodId: number;
}

interface Props {
  periodId: number;
  franchiseId: number;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  'converted': [result: ConvertResult];
  'close': [];
}>();

const { t } = useI18n();
const { createCashAdvancesBatch } = useEmployeeAdvances();
const { fetchAvailableRecebimentos } = usePayrollAdvances();
const { formatDate } = useDateFormatter();

const isVisible = ref<boolean>(false);
const isLoading = ref<boolean>(false);
const isConverting = ref<boolean>(false);
const searchQuery = ref<string>('');
const sortBy = ref<string>('date_desc');
const recebimentos = ref<Recebimento[]>([]);
const selectedRecebimentos = ref<Set<number | string>>(new Set());

interface SortOption {
  label: string;
  value: string;
}

const sortOptions = ref<SortOption[]>([
  { label: t('Date (newest)'), value: 'date_desc' },
  { label: t('Date (oldest)'), value: 'date_asc' },
  { label: t('Employee (A-Z)'), value: 'employee_asc' },
  { label: t('Amount (highest)'), value: 'amount_desc' },
  { label: t('Amount (lowest)'), value: 'amount_asc' }
]);

const selectedRecebimentosArray = computed<Recebimento[]>({
  get: () => recebimentos.value.filter(r => selectedRecebimentos.value.has(r.recebimento_id)),
  set: (value: Recebimento[]) => {
    selectedRecebimentos.value = new Set(value.map(r => r.recebimento_id));
  }
});

const filteredRecebimentos = computed((): Recebimento[] => {
  let filtered = [...recebimentos.value];

  if (searchQuery.value.trim()) {
    const query = searchQuery.value.toLowerCase().trim();
    filtered = filtered.filter(r =>
      (r.operador_name && r.operador_name.toLowerCase().includes(query)) ||
      (r.cliente_nome && r.cliente_nome.toLowerCase().includes(query))
    );
  }

  filtered.sort((a, b) => {
    switch (sortBy.value) {
      case 'date_desc':
        return new Date(b.recebimento_data ?? '').getTime() - new Date(a.recebimento_data ?? '').getTime();
      case 'date_asc':
        return new Date(a.recebimento_data ?? '').getTime() - new Date(b.recebimento_data ?? '').getTime();
      case 'employee_asc':
        return (a.operador_name || '').localeCompare(b.operador_name || '');
      case 'amount_desc':
        return b.amount_reais - a.amount_reais;
      case 'amount_asc':
        return a.amount_reais - b.amount_reais;
      default:
        return 0;
    }
  });

  return filtered;
});

const convertLabel = computed((): string => {
  return selectedRecebimentos.value.size > 0
    ? `${t('Convert to Advances')} (${selectedRecebimentos.value.size})`
    : t('Convert to Advances');
});

const totalSelectedAmount = computed((): number => {
  return Array.from(selectedRecebimentos.value)
    .map(id => recebimentos.value.find(r => r.recebimento_id === id))
    .filter((r): r is Recebimento => !!r)
    .reduce((sum, r) => sum + (parseFloat(String(r.amount_reais)) || 0), 0);
});

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
};

const formatDateStr = (dateStr: string | undefined): string => {
  if (!dateStr) return '-';
  return formatDate(new Date(dateStr));
};

const loadRecebimentos = async (): Promise<void> => {
  isLoading.value = true;
  try {
    log('CashAdvancesSelectionDialog', 'loadRecebimentos', 'Fetching', { periodId: props.periodId });
    const dados = (await fetchAvailableRecebimentos(props.periodId)) as unknown as Recebimento[];
    recebimentos.value = dados || [];
    log('CashAdvancesSelectionDialog', 'loadRecebimentos', 'Success', { count: recebimentos.value.length });
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    log('CashAdvancesSelectionDialog', 'loadRecebimentos', 'Error', err);
    eventBus.emit('globalMessage.error', { message: message || t('Failed to load cash recebimentos'), duration: 5000 });
    recebimentos.value = [];
  } finally {
    isLoading.value = false;
  }
};

const selectAllVisible = (): void => {
  filteredRecebimentos.value.forEach(r => {
    selectedRecebimentos.value.add(r.recebimento_id);
  });
};

const clearSelection = (): void => {
  selectedRecebimentos.value.clear();
};

const handleConvert = async (): Promise<void> => {
  if (selectedRecebimentos.value.size === 0) return;

  isConverting.value = true;
  try {
    log('CashAdvancesSelectionDialog', 'handleConvert', 'Converting', { count: selectedRecebimentos.value.size });
    const result = await createCashAdvancesBatch(
      props.periodId,
      Array.from(selectedRecebimentos.value),
      props.franchiseId
    );
    if (result && result.created_count > 0) {
      eventBus.emit('globalMessage.success', {
        message: t('Created {count} advances successfully', { count: result.created_count }),
        duration: 5000
      });
      emit('converted', result as unknown as ConvertResult);
      closeDialog();
    } else {
      throw new Error('Falha ao criar adiantamentos');
    }
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    log('CashAdvancesSelectionDialog', 'handleConvert', 'Error', err);
    eventBus.emit('globalMessage.error', { message: message || t('Failed to convert recebimentos to advances'), duration: 5000 });
  } finally {
    isConverting.value = false;
  }
};

const closeDialog = (): void => {
  isVisible.value = false;
  emit('close');
};

const handleVisibleChange = (value: boolean): void => {
  isVisible.value = value;
  if (!value) closeDialog();
};

const handleOpen = (data?: unknown): void => {
  const payload = data as OpenPayload | undefined;
  if (payload && payload.periodId === props.periodId) {
    isVisible.value = true;
    loadRecebimentos();
  }
};

onMounted(() => {
  eventBus.on('modal.cash-advances-selection.open', handleOpen);
});

onBeforeUnmount(() => {
  eventBus.off('modal.cash-advances-selection.open', handleOpen);
});
</script>

<style scoped>
.cash-advances-selection {
  min-height: 400px;
}

:deep(.p-datatable) {
  font-size: 0.875rem;
}

:deep(.p-datatable .p-datatable-tbody > tr > td) {
  padding: 0.5rem;
}

:deep(.p-tag) {
  font-size: 0.75rem;
}
</style>
